import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { URL } from '../constants/service-urls';

export interface BotChip {
  label: string;
  action: 'goto' | 'ticket' | 'human' | 'skip_note' | 'confirm_ticket' | 'cancel' | 'end' | 'back' | 'menu' | 'rate';
  next?: string;
}

export interface BotMessage {
  from: 'bot';
  text: string;
}

export type BotMode =
  | 'bot' | 'awaiting_email' | 'awaiting_phone' | 'awaiting_note' | 'awaiting_confirm' | 'human' | 'ended';

export interface BotReply {
  session_id: string;
  messages: BotMessage[];
  chips: BotChip[];
  unresolved: number;
  suggest_escalation: boolean;
  mode: BotMode;
  user?: { name?: string; email?: string; phone?: string };
  ticket?: { id: number; subject: string };
  handoff?: { user_id: number; org_id: number };
}

export interface BotContext {
  user_id: number;
  organization_id: number;
  user_name: string;
  email: string;
  phone: string;
  role: string;
  org_name: string;
}

export interface BotHandoff { user_id: number; org_id: number; }
export interface BotIdentity { name?: string; email?: string; phone?: string; }

@Injectable({ providedIn: 'root' })
export class SupportBotService {
  constructor(private api: ApiService) {}

  start(context: BotContext): Observable<BotReply> {
    return this.api.post(URL.SUPPORT_BOT.START, { requestBody: context }).pipe(map(this.unwrap));
  }

  sendChip(sessionId: string, chip: BotChip): Observable<BotReply> {
    return this.api.post(URL.SUPPORT_BOT.MESSAGE, {
      requestBody: { session_id: sessionId, type: 'chip', action: chip.action, next: chip.next },
    }).pipe(map(this.unwrap));
  }

  sendText(sessionId: string, text: string): Observable<BotReply> {
    return this.api.post(URL.SUPPORT_BOT.MESSAGE, {
      requestBody: { session_id: sessionId, type: 'text', text },
    }).pipe(map(this.unwrap));
  }

  end(sessionId: string): Observable<any> {
    return this.api.post(URL.SUPPORT_BOT.END, { requestBody: { session_id: sessionId } });
  }

  // same unwrapping convention as SupportChatService: res.body ?? res, then .data
  private unwrap = (res: any): BotReply => {
    const payload = res && res.body !== undefined ? res.body : res;
    return (payload && payload.data ? payload.data : payload) as BotReply;
  };
}